import { getColor } from '@/shared';
import useStorage from '@/shared/helpers/sessionStorage';
import { ColorContext } from '@/shared/providers/ColorContext';
import { useData } from '@/shared/providers/DataProvider';
import { useMicrocopy } from '@/shared/providers/Microcopy/MicrocopyHook';
import { useThemeSection } from '@/shared/providers/SectionContext';
import { FilterElementStoryblok, FilterStoryblok } from '@/shared/types/storyblok/storyblok-types';
import * as Sentry from '@sentry/nextjs';
import { getStoryblokApi } from '@storyblok/react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState } from 'react';
import Container from '../Container/Container';
import { JobSkeleton } from '../JobListing/Loading';
import JobListingCore from '../JobListing/_components/JobListingCore';
import { MindSkeleton } from '../MindListing/Loading/Loading';
import MindListingCore from '../MindListing/_components/MindListingCore';
import { ProjectSkeleton } from '../ProjectListing/Loading/Loading';
import ProjectListingCore from '../ProjectListing/_components/ProjectListingCore';
import { ServiceSkeleton } from '../ServiceListing/Loading/Loading';
import ServiceListingCore from '../ServiceListing/_components/ServiceListingCore';
import styles from './Filter.module.scss';
import { FilterSkeleton } from './Loading';

function handleValue(value: any, array: FilterElementStoryblok[]) {
    if (!value) return;

    const hasDuplicate = array.some(existingItem => existingItem.uuid === value.uuid);
    if (!hasDuplicate) array.push(value);
}

interface FilterType {
    blok: FilterStoryblok;
    tabId: string | undefined;
}

const Filter: React.FC<FilterType> = ({ blok, tabId }) => {
    const { filter, sorting, maxDisplayedItems } = blok as any;
    const theme = useThemeSection();
    const isDark = theme === 'Dark';
    const hexColor = useContext(ColorContext);
    const pageColor = getColor(hexColor, isDark);
    const [activeFilter, setActiveFilter] = useState<FilterElementStoryblok>();
    const [content, setContent] = useState<any[]>([]);
    const [filteredContent, setFilteredContent] = useState<any[]>([]);
    const [defaultOption, setDefaultOption] = useState('All,');
    const { version } = useData();
    const [subFilters, setSubFilters] = useState<FilterElementStoryblok[]>([]);
    const router = useRouter();
    const uniqueId = `${router.locale}-${filter?.content?.folderSlug}-${filter?.content?._uid}`;
    const uniqueDataId = `data-${uniqueId}`;
    const uniqueFilterId = `filter-${uniqueId}`;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { microcopy } = useMicrocopy();
    const { getItem, setItem } = useStorage();

    const fetchStories = async () => {
        const savedData = JSON.parse(getItem(uniqueDataId) || '[]');
        const savedFilters = JSON.parse(getItem(uniqueFilterId) || '[]');
        if (savedData.length) {
            setContent(savedData);
            setSubFilters(savedFilters);
            return;
        } else {
            try {
                const cv = Date.now();
                const storyblokApi = getStoryblokApi();
                const response = await storyblokApi.get('cdn/stories', {
                    version: version,
                    cv,
                    starts_with: filter.content.folderSlug,
                    language: router.locale as string,
                    per_page: 100,
                    resolve_relations: [
                        'job.jobLocation',
                        'job.jobField',
                        'mind.mindLocation',
                        'mind.department',
                        'projects.mainDeliverable',
                        'projects.industry',
                        'projects.typeOfClient',
                        'service.type',
                    ],
                });

                const array: FilterElementStoryblok[] = [];
                response.data.stories.forEach((item: FilterElementStoryblok) => {
                    const value = item.content[filter.slug] ?? item.content['type'];
                    if (Array.isArray(value)) value.forEach(singleValue => handleValue(singleValue, array));
                    else if (typeof value === 'object' && value) handleValue(value, array);
                });
                setItem(uniqueFilterId, JSON.stringify(array));
                setItem(uniqueDataId, JSON.stringify(response.data.stories));

                setSubFilters(array);
                setContent(response.data.stories);
                setTimeout(() => {
                    setIsLoading(false);
                }, 300);
            } catch (error) {
                Sentry.captureException('error in fetch stories', {
                    tags: {
                        component: 'filter',
                    },
                    level: 'error',
                });
            }
        }
    };

    useEffect(() => {
        if (microcopy) setDefaultOption(microcopy.filterDefaultOption);
    }, [microcopy]);

    useEffect(() => {
        if (!activeFilter || !filter) {
            return setFilteredContent(content.filter(element => !element?.is_startpage));
        }

        const filteredData = content.filter(item => {
            const value = item.content[filter.slug] ?? item.content['type'];
            if (Array.isArray(value)) return value.some(obj => obj.uuid === activeFilter.uuid);
            return value?.uuid === activeFilter.uuid;
        });

        setFilteredContent(filteredData.filter(element => !element?.is_startpage));
    }, [activeFilter, content, filter]);

    useEffect(() => {
        fetchStories();
        if (isLoading && getItem(uniqueDataId)) {
            setTimeout(() => setIsLoading(false), 500);
        }
    }, [router.locale]); //eslint-disable-line

    return (
        <Container
            code="fullpage"
            blok={blok}
            className={{
                root: clsx(styles.root, { [styles.dark]: isDark }),
            }}
        >
            <Container code="standard" className={{ wrap: styles.wrap }} style={{ wrap: { color: pageColor } }}>
                <div className={clsx(isLoading ? styles.showContent : styles.hiddenLoader)}>
                    <FilterSkeleton items={2} />
                </div>
                <div
                    style={{ display: 'flex', flexWrap: 'wrap' }}
                    className={clsx(!isLoading ? styles.showContent : styles.hiddenContent)}
                >
                    <span
                        onClick={() => setActiveFilter(undefined)}
                        className={clsx(styles.filter, { [styles.active]: !activeFilter })}
                    >
                        {defaultOption}
                    </span>
                    {subFilters.map((item, index) => (
                        <span
                            onClick={() => setActiveFilter(item)}
                            key={item.id}
                            className={clsx(styles.filter, {
                                [styles.active]: activeFilter === item,
                            })}
                        >
                            {(item.content.name || item.name) + (index !== subFilters.length - 1 ? ',' : '')}
                        </span>
                    ))}
                </div>
            </Container>
            <Container code="fullpage" className={{ wrap: styles.filteredContent }}>
                <>
                    {filter?.content?.folderSlug === 'minds/' && (
                        <>
                            <div className={clsx(isLoading ? styles.showContent : styles.hiddenLoader)}>
                                <MindSkeleton items={20} isDark={isDark} />
                            </div>
                            <div className={clsx(!isLoading ? styles.showContent : styles.hiddenContent)}>
                                <MindListingCore
                                    uniqueId={
                                        activeFilter?.name ? `${tabId + activeFilter?.name}`.replace(/\s+/g, '') : tabId
                                    }
                                    content={filteredContent}
                                    sortBy={sorting}
                                    loadMoreButton={microcopy.loadMoreButton}
                                    startingItems={Number(maxDisplayedItems || 100)}
                                />
                            </div>
                        </>
                    )}
                    {filter?.content?.folderSlug === 'projects/' && (
                        <>
                            <div className={clsx(isLoading ? styles.showContent : styles.hiddenLoader)}>
                                <ProjectSkeleton items={18} isDark={isDark} />
                            </div>
                            <div className={clsx(!isLoading ? styles.showContent : styles.hiddenContent)}>
                                <ProjectListingCore
                                    content={filteredContent}
                                    sortBy={sorting}
                                    loadMoreButton={microcopy.loadMoreButton}
                                    startingItems={Number(maxDisplayedItems || 100)}
                                />
                            </div>
                        </>
                    )}
                    {filter?.content?.folderSlug === 'careers/' && (
                        <>
                            <div className={clsx(isLoading ? styles.showContent : styles.hiddenLoader)}>
                                <JobSkeleton items={5} isDark={isDark} />
                            </div>

                            <div className={clsx(!isLoading ? styles.showContent : styles.hiddenContent)}>
                                <JobListingCore
                                    content={filteredContent}
                                    sortBy={sorting}
                                    loadMoreButton={microcopy.loadMoreButton}
                                    startingItems={Number(maxDisplayedItems || 100)}
                                />
                            </div>
                        </>
                    )}
                    {filter?.content?.folderSlug === 'service/' && (
                        <>
                            <div className={clsx(isLoading ? styles.showContent : styles.hiddenLoader)}>
                                <ServiceSkeleton items={20} isDark={isDark} />
                            </div>
                            <div className={clsx(!isLoading ? styles.showContent : styles.hiddenContent)}>
                                <ServiceListingCore
                                    content={filteredContent}
                                    sortBy={sorting}
                                    startingItems={Number(maxDisplayedItems || 100)}
                                />
                            </div>
                        </>
                    )}
                </>
            </Container>
        </Container>
    );
};

export default Filter;
